import {
  createContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { BrowserRouter } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { ThemeProvider } from '@emotion/react'
import styled from '@emotion/styled'
import TagManager from 'react-gtm-module'
import {
  fetchConfig,
  useAppDispatch,
  selectConfig,
  useAppSelector,
  selectCustomer,
} from '@open-tender/cloud'
import GlobalStyles from './GlobalStyles'
import AppRoutes from './components/AppRoutes'
import {
  Alerts,
  CartButton,
  ErrorBoundary,
  Modal,
  Nav,
  NavSite,
  Notifications,
  Sidebar,
  WeglotProvider,
} from './components'
import { ErrorFatal } from './components/pages'
import { generateRandomSessionID, isFarmerBoysBrand } from 'utils/helpers'
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3'
import { useAppHasRecpatcha } from 'hooks'

export const AppContext = createContext(null)

export const AppView = styled.div`
  label: AppView;
  height: 100%;
  display: flex;
  justify-content: flex-end;
`

const baseUrl = process.env.REACT_APP_API_URL
const authUrl = process.env.REACT_APP_AUTH_URL
const clientId = process.env.REACT_APP_CLIENT_ID
const brandId = process.env.REACT_APP_BRAND_ID
const WeglotApiKey = process.env.REACT_APP_WEGLOT_API_KEY
const recaptchaKey = process.env.REACT_APP_ENTERPRISE_RECAPTCHA_KEY
const sessionID = generateRandomSessionID().toString()

const App = () => {
  const dispatch = useAppDispatch()
  const windowRef = useRef<HTMLDivElement>(null)
  const [gtmId, setGtmId] = useState<boolean>(false)
  const { loading, theme, brand, error } = useAppSelector(selectConfig)
  const { profile } = useAppSelector(selectCustomer)
  const appHasRecpatcha = useAppHasRecpatcha()
  const { body, headings } = theme?.fonts || {}
  const isLoading = loading === 'pending'
  const hasTheme = !isLoading && !error && theme
  const hasBrand = brand && body && headings

  useEffect(() => {
    const options = { baseUrl, authUrl, clientId, brandId }
    dispatch(fetchConfig({ ...options, isApp: false }))
  }, [dispatch])
  useEffect(() => {
    window.localStorage.setItem('session_id', sessionID)
  }, [])
  useLayoutEffect(() => {
    if (loading !== 'pending' && brand && !gtmId) {
      setGtmId(true)
      const tagManagerArgs = {
        gtmId: brand.gtmContainerId,
        dataLayer: {
          user_id: profile?.customer_id,
          session_id: sessionID,
        },
      }
      TagManager.initialize(tagManagerArgs)
      TagManager.initialize({ ...tagManagerArgs, gtmId: brand.gtmContainer2Id })
    }
  }, [loading, brand, gtmId, profile?.customer_id])

  return (
    <WeglotProvider apiKey={WeglotApiKey} disabled>
      <ErrorFatal error={error} loading={loading} />
      {hasTheme && (
        <GoogleReCaptchaProvider
          useEnterprise={brand?.use_enterprise_recaptcha}
          reCaptchaKey={appHasRecpatcha ? recaptchaKey || '' : ''}
        >
          <ThemeProvider theme={theme}>
            <GlobalStyles />
            <AppView ref={windowRef} id="app">
              {hasBrand ? (
                <Helmet>
                  {isFarmerBoysBrand && (
                    <script src="https://polaris.truevaultcdn.com/static/pc/5ISCOAGMT/polaris.js" />
                  )}
                  <title>{brand.title}</title>
                  <meta name="description" content={brand.description} />
                  <link rel="icon" href={brand.favicon} />
                  <link rel="apple-touch-icon" href={brand.appleTouchIcon} />
                  <link href={body.url} rel="stylesheet" />
                  {headings.url && body.url !== headings.url && (
                    <link href={headings.url} rel="stylesheet" />
                  )}
                </Helmet>
              ) : null}
              <AppContext.Provider value={{ windowRef: window } as any}>
                <ErrorBoundary>
                  <BrowserRouter>
                    <Modal />
                    <Alerts />
                    <Notifications />
                    <CartButton />
                    <AppRoutes />
                    <Sidebar />
                    <Nav />
                    <NavSite />
                  </BrowserRouter>
                </ErrorBoundary>
              </AppContext.Provider>
            </AppView>
          </ThemeProvider>
        </GoogleReCaptchaProvider>
      )}
    </WeglotProvider>
  )
}

export default App
